<template>
  <div>
    <button class="btn btn-green" @click="showModal">+ New Fees</button>

    <!-- Modal -->
    <div class="modal fade" ref="feesModal" tabindex="-1" role="dialog" aria-labelledby="exampleModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-xl custom-modal" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">New Fees</h5>
            <button type="button" class="close" @click="hideModal" aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <div class="row d-flex align-items-stretch">
              <!-- Details Card -->
              <div class="col-md-4 col-12 d-flex">
                <div class="card flex-grow-1">
                  <div class="card-body">
                    <h5 class="section-title">Details</h5>
                    <div class="row">
                      <div class="col-12 d-flex align-items-center">
                        <label for="amount" class="me-2">Amount</label>
                        <span class="input-group-text">$</span>
                        <input type="text" id="amount" class="form-control ms-2" v-model="amount" @input="validateAmount" />
                      </div>
                      <div class="col-12 mt-2">
                        <label for="description">Description</label>
                        <textarea id="description" class="form-control" rows="10" v-model="description"></textarea>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <!-- Assignment Card -->
              <div class="col-md-8 col-12 d-flex">
                <div class="card flex-grow-1">
                  <div class="card-body">
                    <h5 class="section-title">Assignment</h5>
                    <div class="row d-flex">
                      <div class="col-xxl-3 col-md-12 mt-5 align-items-start p-0">
                        <div class="checkbox checkbox-primary mb-2 d-flex align-items-center">
                          <input id="driver-checkbox" type="checkbox" class="form-check-input" v-model="isDriverChecked">
                          <label for="driver-checkbox" class="form-check-label">Driver</label>
                        </div>
                      </div>
                      <div class="col-xxl-4 col-12">
                        <label for="driver-id">Driver ID</label>
                        <input type="text" id="driver-id" class="form-control" :disabled="!isDriverChecked" @keyup.enter="fetchDriverInfo" v-model="driverId" @input="handleDriverIdChange" data-bs-toggle="tooltip" title="Please press Enter after entering ID" />
                      </div>
                      <div class="col-xxl-5 col-12">
                        <label for="driver-name">Name</label>
                        <input type="text" id="driver-name" class="form-control" :disabled="true" :value="driverNameMessage" />
                      </div>
                    </div>
                    <hr />
                    <div class="row">
                      <div class="col-xxl-3 col-12 checkbox checkbox-primary mt-4 d-flex align-items-center p-0">
                        <input id="subgroup-checkbox" type="checkbox" class="form-check-input" v-model="isSubgroupChecked">
                        <label for="subgroup-checkbox" class="form-check-label">Subgroup</label>
                      </div>
                      <div class="col-xxl-9 col-12">
                        <label for="subgroup-driver-id">Subgroup name</label>
                        <select id="subgroup-driver-id" class="form-select" :disabled="!isSubgroupChecked" v-model="selectedSubgroup">
                          <option value="" disabled>Select...</option>
                          <option v-for="subgroup in subgroupStore.hasSubgroups" :value="subgroup.id">{{ subgroup.name }}</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-ok" @click="submitForm" :disabled="!canSubmit">OK</button>
            <button class="btn btn-cancel" @click="hideModal">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed ,onMounted } from 'vue';
import { Modal , Tooltip } from 'bootstrap';
import { useHttp } from '@/http/http';
import { statusCheck } from '@/store/public'; // 確保這是正確的路徑
import { useSubgroupStore } from '@/store/admin/subgroup';
import { useFeeAndRefundStore } from '@/store/admin/feeAndRefund';
import Swal from 'sweetalert2';


const subgroupStore = useSubgroupStore();
const feeAndRefundStore = useFeeAndRefundStore();

const fetchSubgroups = async () => {
  await subgroupStore.fetchSubgroups(1, 9999);
};

// 初始化狀態變數
const amount = ref('');
const description = ref('');
const isDriverChecked = ref(false);
const isSubgroupChecked = ref(false);
const driverId = ref('');
const driverName = ref('');
const selectedSubgroup = ref('');
const validAmount = ref(false);
const driverInfoFetched = ref(false);

// 動態顯示的 driver 名稱或錯誤信息
const driverNameMessage = computed(() => {
  if (driverId.value && !driverName.value) {
    return '';
  }
  return driverName.value;
});

// 根據選擇狀態來確定 OK 按鈕是否可用
const canSubmit = computed(() => {
  return (
    ((isDriverChecked.value && driverName.value && driverInfoFetched.value) || 
    (isSubgroupChecked.value && selectedSubgroup.value)) &&
    validAmount.value && amount.value && description.value
  );
});

// 監控 isDriverChecked 的變化
watch(isDriverChecked, (newValue) => {
  if (newValue) {
    isSubgroupChecked.value = false;
    selectedSubgroup.value = ''; // 清空 Subgroup 下拉選單
  }
});

// 監控 isSubgroupChecked 的變化
watch(isSubgroupChecked, (newValue) => {
  if (newValue) {
    fetchSubgroups();
    isDriverChecked.value = false;
    driverId.value = '';
    driverName.value = ''; // 清空 driver name
  }
});

// 驗證 amount 格式
const validateAmount = (event) => {
  let value = event.target.value;

  // 儲存當前游標位置
  const cursorPosition = event.target.selectionStart;

  // 移除非數字字符，並確保不是負數
  value = value.replace(/[^0-9.]/g, ''); // 僅保留數字和小數點
  if (value.startsWith('-')) {
    value = ''; // 禁止負數
  }

  // 檢查總位數是否符合要求
  const regex = /^\d{0,8}(\.\d{0,2})?$/;
  if (regex.test(value)) {
    validAmount.value = true;
    amount.value = value;
  } else {
    validAmount.value = false;
    Swal.fire({
      icon: 'error',
      title: 'The input number format is incorrect',
      text: 'Please ensure the total length is 10 digits, with 8 digits before the decimal and 2 digits after.',
    });
  }

  // 更新輸入框中的值並保持游標位置
  setTimeout(() => {
    event.target.value = value;
    event.target.setSelectionRange(cursorPosition, cursorPosition);
  });
};

// 當 driverId 改變時，將 driverInfoFetched 設為 false
const handleDriverIdChange = () => {
  driverInfoFetched.value = false;
  driverName.value = ''; // 清空 driver name
};

// Modal 操作
const feesModal = ref(null);
let modalInstance = null;

const showModal = () => {
  resetForm(); // 清空表單
  if (!modalInstance) {
    modalInstance = new Modal(feesModal.value);
  }
  modalInstance.show(); // 顯示模態框
};

const hideModal = () => {
  if (modalInstance) {
    modalInstance.hide(); // 隱藏模態框
  }
};

// Tooltip 初始化
onMounted(() => {
  const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
  tooltipTriggerList.forEach(tooltipTriggerEl => {
    new Tooltip(tooltipTriggerEl);
  });
});

const resetForm = () => {
  amount.value = '';
  description.value = '';
  isDriverChecked.value = false;
  isSubgroupChecked.value = false;
  driverId.value = '';
  driverName.value = '';
  selectedSubgroup.value = '';
  validAmount.value = false;
  driverInfoFetched.value = false;
};

// 提交表單
const submitForm = async () => {
  const payload = {
    type: 'fee', 
    driver_id: isDriverChecked.value ? driverId.value : null,
    subgroup_id: isSubgroupChecked.value ? selectedSubgroup.value : null,
    amount: amount.value,
    description: description.value
  };

  try {
    await feeAndRefundStore.createFeesAndRefund(payload);
    resetForm(); // 清空表單
    await feeAndRefundStore.resetFilters(); // 重置篩選條件
    hideModal();
  } catch (error) {
    console.error('Failed to create fee:', error);
  }
};

// 根據 driverId 獲取 driver 信息
const fetchDriverInfo = async () => {
  try {
    const response = await useHttp().get(`api/admin/driver/info?driver_id=${driverId.value}`);
    const driver = response.data.result;
    if (driver) {
      driverName.value = driver.first_name + ' ' + driver.middle_name + ' ' + driver.last_name;
      driverInfoFetched.value = true; // 正確獲取 driver 信息
    } else {
      driverName.value = 'Name is not found';
    }
  } catch (error) {
    driverName.value = 'Name is not found';
    driverInfoFetched.value = false;
    console.error('Error fetching driver:', error);
  }
};
</script>



<style scoped>
.btn-green {
  background: rgba(0, 201, 98, 1);
  color: black;
  border: none;
  width: 224.83px;
  height: 46px;
  border-radius: 30px;
  padding: 10px, 32px, 10px, 32px;
}

.btn-green:hover {
  background-color: #00b452;
}

.btn-ok{
  background: rgba(0, 201, 98, 1);
  color: black;
  border: none;
  width: 150px;
  height: 46px;
  border-radius: 30px;
  padding: 10px, 32px, 10px, 32px;
}

.btn-ok:hover {
  background-color: #00b452;
}

.btn-cancel {
/* BTN_B */

  box-sizing: border-box;

  /* Auto layout */
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 14px 32px;
  gap: 10px;

  width: 150px;
  height: 46px;

  /* Indigo */
  border: 2px solid #5248DA;
  border-radius: 30px;

  /* Inside auto layout */
  flex: none;
  order: 1;
  flex-grow: 0;

}

.btn-cancel:hover {
  border: 2px solid #5248DA;
}



.modal-content {
  border-radius: 10px;
  padding: 20px;
}

.modal-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-footer {
  border-top: 1px solid #dee2e6;
  background-color: #f8f9fa;
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
}

.card {
  background-color: #d9dde3;
  border-radius: 10px;
}

.section-title {
  font-size: 1.2rem;
  margin-bottom: 15px;
}

.form-group {
  margin-bottom: 15px;
}

.form-control, .form-select {
  border-radius: 5px;
  padding: 10px;
  border: 1px solid #ced4da;
}

.close {
  color: #333;
  opacity: 1;
  font-size: 20px;
  padding: 0;
  background: none;
  border: none;
}

.close:hover {
  color: #000;
}

/* 使用 flexbox 來讓卡片等高 */
.row.d-flex {
  display: flex;
}

.card.flex-grow-1 {
  flex-grow: 1;
}

/* 自定義模態框寬度和高度 */
.modal-dialog.custom-modal {
  max-width: 80%; /* 自定義寬度 */
  width: 80%;
  max-height: 90vh; /* 自定義最大高度 */
}

.modal-content {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.modal-body {
  flex-grow: 1;
  overflow-y: auto;
}

.modal-header, .modal-footer {
  flex-shrink: 0;
}

/* 自定義 checkbox */
.checkbox-primary .form-check-input {
  width: 35px;
  height: 35px;
  border: 2px solid #8476f4; /* 紫色邊框 */
  border-radius: 5px;
  background-color: white; /* 背景色為白色 */
  -webkit-appearance: none; /* 移除預設樣式 */
  appearance: none; /* 移除預設樣式 */
  cursor: pointer;
  position: relative;
  vertical-align: middle;
}

.checkbox-primary .form-check-input:checked {
  background-color: #8476f4; /* 選中時的背景色 */
  border-color: #8476f4; /* 選中時的邊框色 */
}

.checkbox-primary .form-check-input:checked::before {
  content: '✓';
  color: white;
  font-weight: bold;
  font-size: 18px;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -60%); /* 修正勾號位置 */
}

/* 調整標籤文字大小並與 checkbox 對齊 */
.checkbox-primary .form-check-label {
  font-size: 18px; /* 調整文字大小 */
  margin-left: 10px; /* 與 checkbox 保持適當距離 */
  vertical-align: middle; /* 垂直對齊 */
}

/* 在小於 768px 的裝置上讓 modal 佔滿螢幕 */
@media (max-width: 768px) {
  .modal-dialog.custom-modal {
    max-width: 100%;
    width: 100%;
    height: 100vh;
    margin: 0;
  }

  .modal-content {
    height: 100%;
    border-radius: 0; /* 移除圓角確保佔滿螢幕 */
  }

  .modal-body {
    overflow-y: auto;
  }
}
</style>
